import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'

import { IQuestionComponentProps, IQuestionComponentRef, QuestionViewFrame } from '../types.ts'
import {
  EcommerceQuestionModelTypes,
  EcommerceStoreType,
  EcommerceSubmitResponse,
  EcommerceResponse,
  IEcommerceAnswerSchema,
  IEcommerceResponseValidation,
  EcommerceStoreViews
} from './types.ts'

import useViewport from '../../modules/viewport/useViewport.ts'
import useForceImmutable from '@aytm/lib/hooks/useForceImmutable'
import useSession from '../../session/useSession.ts'
import useTimeline from '../../modules/timeline/useTimeline.ts'
import useTheme from '@aytm/app/swidget/base/modules/theme/useTheme'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'

import QuestionFrame from '../_common/QuestionFrame/QuestionFrame.tsx'
import { getMediumAssetPath, getOldMediumAssetPath } from '@aytm/lib/common/assets'
import { layoutIsCompact } from '../../lib/layout.ts'
import TextWithOptions from '../../modules/preview/components/TextWithOptions.tsx'
import usePreviewOptions from '../../modules/preview/usePreviewOptions.ts'
import ECommerceView from './Ecommerce/ECommerceView.tsx'
import AmazonView from './Amazon/AmazonView.tsx'
import WalmartView from './Walmart/WalmartView.tsx'
import TargetView from './Target/TargetView.tsx'
import NikeView from './Nike/NikeView.tsx'
import ClosedShelfView from './View.tsx'
import IntroShelfView from './IntroView.tsx'
import StandartView from './Standart/StandartView.tsx'
import CostcoView from './Costco/CostcoView.tsx'
import ChewyView from './Chewy/ChewyView.tsx'
import KrogerView from './Kroger/KrogerView.tsx'
import SamsClubView from './SamsClub/SamsClubView.tsx'
import TescoView from './Tesco/TescoView.tsx'
import textWithOptionsOld from '../../modules/preview/functions/textWithOptionsOld.ts'
import imageAssetWithOptions from '../../modules/preview/functions/imageAssetWithOptions.ts'
import { markHiddenProducts } from './utils/familyTree.ts'

const STORE_VIEWS: EcommerceStoreViews = {
  amazonw: { View: AmazonView, name: 'Amazon' },
  targetw: { View: TargetView, name: 'Target' },
  walmartw: { View: WalmartView, name: 'Walmart' },
  nikew: { View: NikeView, name: 'Nike' },
  ecommercew: { View: StandartView, name: 'Ecommerce' },
  costcow: { View: CostcoView, name: 'Costco' },
  chewyw: { View: ChewyView, name: 'Chewy' },
  krogerw: { View: KrogerView, name: 'Kroger' },
  samsclubw: { View: SamsClubView, name: "Sam's Club" },
  tescow: { View: TescoView, name: 'Tesco' }
}

/* eslint-disable @typescript-eslint/no-unsafe-assignment,
@typescript-eslint/no-explicit-any,
@typescript-eslint/no-unsafe-member-access,
@typescript-eslint/no-unsafe-call,
@typescript-eslint/no-var-requires */
const SH = require('../../../questions/components/sh')
const OldEcommerceQuestion: any = SH.default

const EcommerceQuestion = React.memo(
  React.forwardRef<IQuestionComponentRef, IQuestionComponentProps<EcommerceQuestionModelTypes>>(({ model }, ref) => {
    const [[response, responseValidation, __frame], setResponse] = useState<
      [EcommerceResponse, IEcommerceResponseValidation, QuestionViewFrame]
    >([model.response, model.responseValidation, model.frame])

    const session = useSession()
    const viewport = useViewport()
    const timeline = useTimeline()
    const theme = useTheme()
    const { t } = useSurveyLocale()
    const { localeID } = useSurveyLocale()
    const previewOptions = usePreviewOptions()
    const [closed, setClosed] = useState(false)
    const [intro, setIntro] = useState(false)
    const findability =
      'find' == model.schema.mode_type || (1 == model.schema.max_cart_size && !model.schema.shopping_cart)

    const [showValidation, setShowValidation] = useState(false)
    const [width, setWidth] = useState(viewport.mobile ? Math.min(viewport.width, 600) : 600)
    const [widgetWidth, setWidgetWidth] = useState<number>(null)

    const parentRef = useRef(null)

    const doNext = () => {
      session.enableNext(model.responseValidation?.valid)
      session.doNext()
    }

    useEffect(() => {
      setWidth(viewport.mobile ? Math.min(viewport.width, widgetWidth || viewport.width) : widgetWidth || 600)
    }, [viewport.width, viewport.mobile, widgetWidth])

    const subRules = session.rules?.sub?.[model.schema.id]
    const schema = useRef(null)
    if (!schema.current)
      schema.current = (() => {
        const sc = { ...model.schema }
        setIntro(true)
        model.schema.answers.forEach((answer, index) => {
          answer.index = index
          if (answer.image) answer.image.trimmed_path = getOldMediumAssetPath(answer.image)
          if (answer.gallery) answer.gallery.forEach(image => (image.trimmed_path = getOldMediumAssetPath(image)))
          answer.available = true
        })
        const questionText = textWithOptionsOld(
          model.schema.text,
          previewOptions,
          subRules,
          session.rules?.hide_text?.[model.schema.id]
        )
        // the schema permits a nil instruction; interpolating one renders a literal 'null' in the hint
        sc.instruction = model.schema.instruction ? `${questionText}<br/>${model.schema.instruction}` : questionText

        return sc
      })()

    // researcher preview: in-text commands / pipes arrive raw, resolved in rules (live resolves server-side),
    // and rules.show[id] marks what live would have cut (markHiddenProducts). Substitution and marks depend on
    // the preview toggles, so they re-derive from the kept raw text/image; when nothing selectable is left
    // (every top-level product hidden) Next stays open, like RB.
    const everyProductHidden = useMemo(() => {
      schema.current.answers.forEach((answer: IEcommerceAnswerSchema) => {
        const subRule = subRules?.[answer.id]
        const hideText = session.rules?.hide_text?.[answer.id]
        if (subRule || hideText) {
          answer.rawText ??= answer.text // answers are shared with the action payload; a remount runs this again
          answer.text = textWithOptionsOld(answer.rawText, previewOptions, subRule, hideText)
        }
        if (subRule) {
          answer.rawImage ??= answer.image
          answer.image = imageAssetWithOptions(subRule, answer.rawImage)
          if (answer.image && !answer.image.trimmed_path)
            answer.image.trimmed_path = getOldMediumAssetPath(answer.image)
        }
      })
      markHiddenProducts(schema.current.answers, session.rules?.show, previewOptions)
      return schema.current.answers.every(
        (answer: IEcommerceAnswerSchema) => answer.parent_product || answer.na || answer.hidden || answer.fakeHidden
      )
    }, [
      subRules,
      session.rules?.show,
      session.rules?.hide_text,
      previewOptions.showHidden,
      previewOptions.suppressLogic,
      previewOptions.showLogic
    ])

    useEffect(() => {
      session.enableNext(model.responseValidation?.valid || intro || everyProductHidden)
    }, [session, model.responseValidation?.valid, model.frame, intro, everyProductHidden])

    function keyDownHandler(event: globalThis.KeyboardEvent) {
      if ('Escape' === event.key) {
        event.preventDefault()
        setClosed(true)
      }
    }
    useEffect(() => {
      if (session.action.preview) document.addEventListener('keydown', keyDownHandler)

      return () => {
        document.removeEventListener('keydown', keyDownHandler)
      }
    }, [session.action.preview])

    const changeWidth = (width: number) => {
      session.setWidgetWidth(width)
      setWidgetWidth(width)
    }

    const questionTitle = (
      <>
        {previewOptions.showNumbers ? (
          <span data-testid="SW-Question-ID">{model.schema.id.toUpperCase()}. </span>
        ) : null}
        {previewOptions.showHidden || !session.rules?.hide_text?.[model.schema.id]?.condition ? (
          <TextWithOptions
            text={model.schema.text}
            options={previewOptions}
            rules={session.rules?.sub?.[model.schema.id]?.[model.schema.id]}
          />
        ) : null}
      </>
    )

    const logEvent = (ev: any) => {
      timeline.log({ type: ev.type, ref: ev.ref, target: ev.target })
    }

    const onUpdateResponse = (response: EcommerceResponse) => {
      model.response = { ...response }
      setResponse([model.response, model.responseValidation, model.frame])
      if (findability && model.responseValidation.valid) {
        doNext()
        return
      }
      setShowValidation(false)
    }

    const onUpdateOldResponse = (answer: any, validation: any) => {
      model.responseValidation.valid = validation.get('valid')
      model.response = {
        answers: answer.get('answers').reduce((prev: EcommerceSubmitResponse, cur: any) => {
          if (cur.get('count')) prev.push({ id: cur.get('id'), v: cur.get('count') })
          return prev
        }, [])
      }
      setResponse([model.response, model.responseValidation, model.frame])
    }

    useImperativeHandle<IQuestionComponentRef, IQuestionComponentRef>(ref, () => {
      return {
        tryNext() {
          if (intro) {
            session.progress.advanceQuestionSeconds(model.questionTime * 0.3)
            session.enableNext(false)
            setIntro(false)
            return true
          } else if (model.responseValidation?.valid || session.skipValidation || everyProductHidden) {
            return false
          } else {
            session.enableNext(false)
            return true
          }
        },
        tryPrev() {
          return false
        },
        showValidation() {
          setShowValidation(true)
        }
      }
    }, [intro, model.responseValidation?.valid, model.questionTime, session, everyProductHidden])

    const checkout = () => {
      session.doNext()
    }

    const onShown = useCallback(() => {
      session.enableNext(true)
    }, [session])

    const immutableModelSchema = useForceImmutable(schema.current)

    const imageRef = useRef<string[]>(null)
    if (!imageRef.current)
      imageRef.current = model.schema.answers.reduce((array: string[], answer) => {
        if (answer.image) array.push(getMediumAssetPath(answer.image, 'transparent'))
        return array
      }, [])

    const onSimulationClose = () => {
      timeline.log({
        type: 'close_simulation'
      })
      setClosed(true)
    }

    const onSimulationOpen = () => {
      timeline.log({
        type: 'open_simulation'
      })
      setClosed(false)
    }

    if (intro)
      return (
        <QuestionFrame model={model} noImage={true} titleText={t('questions', 'ec', 'introHeader')}>
          <IntroShelfView
            store={schema.current.store}
            findability={findability}
            onShown={onShown}
            storeName={'ecommercew' === schema.current.store ? schema.current.name : null}
          />
        </QuestionFrame>
      )

    if ('ecommerce' == schema.current.store)
      return (
        <QuestionFrame model={model} fullsize={true} images={imageRef.current}>
          <ECommerceView
            schema={schema.current}
            onUpdateResponse={onUpdateResponse}
            response={response}
            responseValidation={responseValidation}
            showValidation={showValidation}
          />
        </QuestionFrame>
      )

    const storeView = STORE_VIEWS[schema.current.store as EcommerceStoreType]
    if (storeView) {
      const { View, name } = storeView
      return (
        <QuestionFrame model={model} fullsize={true} noTitle={true}>
          <div ref={parentRef}>
            {closed ? (
              <ClosedShelfView
                schema={schema.current}
                response={response}
                store={name}
                onAction={onSimulationOpen}
                onUpdateResponse={onUpdateResponse}
              />
            ) : (
              <View
                schema={schema.current}
                onUpdateResponse={onUpdateResponse}
                response={response}
                checkout={checkout}
                responseValidation={responseValidation}
                showValidation={showValidation}
                setClosed={onSimulationClose}
                parentRef={parentRef}
              />
            )}
          </div>
        </QuestionFrame>
      )
    }

    return (
      <QuestionFrame model={model} fullsize={true}>
        <OldEcommerceQuestion
          questionSchema={immutableModelSchema}
          answerChanged={onUpdateOldResponse}
          onWidthChanged={changeWidth}
          onLogEvent={logEvent}
          theme={layoutIsCompact(session.props.layout) ? 'mobile' : viewport.device}
          noHeader={true}
          width={width}
          preview={session.action.preview != null}
          previewMode={session.action.mode}
          emulatedMobile={viewport.emulationMobileFrame || layoutIsCompact(session.props.layout)}
          accentColor={theme.style.accentColor}
          showErrors={showValidation}
          goNext={doNext}
          inApp={session.style?.flutter}
          locale={localeID}
          innerWidth={viewport.width}
          questionTitle={questionTitle}
        />
      </QuestionFrame>
    )
  }),
  (prevProps, nextProps) => prevProps.model === nextProps.model
)

EcommerceQuestion.displayName = 'EcommerceQuestion'

export default EcommerceQuestion
